import React, { Component } from 'react';
import { trim } from 'lodash-es';
import { Form, Input, Button, InputNumber } from 'components/antd';
import { Gap } from 'components';
import classNames from 'classnames';
import styles from './TableBuilder.module.scss';
import { Nullable } from 'app-types';

const FormItem = Form.Item;
const EditableContext = React.createContext({});
interface EditableRowProps {
	form: any;
	index: number;
	addRowTitle: string;
	isEmptyRows?: boolean;
	scroll?: any;
	columns: any;
	handleAddRowInput: any;
	handleAdd: any;
	className?: string;
	setRowClassNames?: (rowIndex: number) => string;
}

interface EditableRowState {
	isAddingNewRow: boolean;
	addingRowParams: any;
}

class EditableRow extends Component<EditableRowProps, EditableRowState> {
	setTextInputRef: any;

	constructor(props: EditableRowProps) {
		super(props);

		this.setTextInputRef = (element: HTMLInputElement) => {
			if (element) {
				element.focus();
			}
		};

		this.state = {
			isAddingNewRow: false,
			addingRowParams: {
				...this.getDefaultValues(),
			},
		};
	}

	getDefaultValues() {
		const { columns } = this.props;

		const defaultValues = columns.reduce((acc: any, column: any) => {
			if (column.defaultValues) {
				return { ...acc, ...column.defaultValues };
			}

			return acc;
		}, {});

		return defaultValues;
	}

	handleAdd = (e: React.SyntheticEvent) => {
		this.closeAddingState(e);
		this.props.handleAdd({ ...this.state.addingRowParams });
	};

	isAddingRowValid = () => {
		const { columns } = this.props;
		const { addingRowParams } = this.state;

		return columns.every((col: any) => {
			if (col.required) {
				const value = addingRowParams[col.dataIndex];
				return value === null || trim(value);
			}

			return true;
		});
	};

	closeAddingState = (e?: React.SyntheticEvent) => {
		if (e) {
			e.stopPropagation();
		}

		this.setState({
			isAddingNewRow: false,
			addingRowParams: this.getDefaultValues(),
		});
	};

	openAddingState = (e?: React.SyntheticEvent) => {
		if (e) {
			e.stopPropagation();
		}
		this.setState({
			isAddingNewRow: true,
		});
	};

	handleAddRowInput = (e: React.ChangeEvent<HTMLInputElement>, fieldName?: string) => {
		if (fieldName) {
			this.setState({
				addingRowParams: {
					...this.state.addingRowParams,
					[fieldName]: e.target.value,
				},
			});
		}
	};

	handleAddRowInputNumber = (value?: Nullable<number | string>, fieldName?: string) => {
		if (fieldName) {
			this.setState({
				addingRowParams: {
					...this.state.addingRowParams,
					[fieldName]: value,
				},
			});
		}
	};

	shouldShowAddRow = (index: number, addRowTitle: string) => {
		return index === 0 && addRowTitle !== undefined;
	};

	showInput = (col: any) => {
		const { isAddingNewRow } = this.state;

		return col.addParam && isAddingNewRow && !col.isSelect && !col.isNumberValue;
	};

	showInputNumber = (col: any) => {
		const { isAddingNewRow } = this.state;

		return col.addParam && isAddingNewRow && col.isNumberValue;
	};

	render() {
		const {
			form,
			index,
			columns,
			addRowTitle,
			isEmptyRows,
			handleAdd,
			scroll,
			setRowClassNames,
			className,
			...restProps
		} = this.props;
		const { isAddingNewRow, addingRowParams } = this.state;
		let rowClassNames = className;
		if (setRowClassNames) {
			rowClassNames += ' ' + setRowClassNames(index);
		}

		return (
			<EditableContext.Provider value={form}>
				{this.shouldShowAddRow(index, addRowTitle) && (
					<tr className={styles.colAddNewRow} onClick={e => this.openAddingState(e)}>
						{columns.map((col: any, index: number) => (
							<td
								className={styles.colAddNewInputWrapper}
								key={`dictionaryColumn-${index}`}
								colSpan={index === 0 && !isAddingNewRow ? columns.length : null}
								hidden={index !== 0 && !isAddingNewRow}
							>
								{index === 0 &&
									!isAddingNewRow && (
										<span className={styles.addRowTitle}>{addRowTitle}</span>
									)}
								{this.showInput(col) && (
									<FormItem>
										<Input
											id={col.dataIndex}
											ref={index === 0 ? this.setTextInputRef : undefined}
											allowClear
											value={addingRowParams[col.addParam]}
											onChange={e => this.handleAddRowInput(e, col.addParam)}
											placeholder={col.titleText}
										/>
									</FormItem>
								)}
								{this.showInputNumber(col) && (
									<FormItem>
										<InputNumber
											id={col.dataIndex}
											ref={index === 0 ? this.setTextInputRef : undefined}
											value={addingRowParams[col.addParam]}
											precision={col.precision || 0}
											step={col.step}
											onChange={value =>
												this.handleAddRowInputNumber(value, col.addParam)
											}
											placeholder={col.titleText}
										/>
									</FormItem>
								)}
								{index === columns.length - 1 &&
									isAddingNewRow && (
										<div className={styles.addRowControls}>
											<Gap right="sm">
												<Button
													type="primary"
													icon="apply"
													size="small"
													onClick={this.handleAdd}
													disabled={!this.isAddingRowValid()}
												/>
											</Gap>
											<Button
												icon="remove"
												size="small"
												onClick={this.closeAddingState}
											/>
										</div>
									)}
							</td>
						))}
					</tr>
				)}

				{isEmptyRows && (
					<tr
						className={classNames('noResults', styles.noResults, {
							[styles.scrollTableNoResults]: Boolean(scroll),
						})}
					>
						<td colSpan={columns.length}>No results</td>
					</tr>
				)}

				{!isEmptyRows && <tr {...restProps} className={rowClassNames} />}
			</EditableContext.Provider>
		);
	}
}

const EditableFormRow = Form.create()(EditableRow);

export { EditableFormRow, EditableContext };
